import React, {useCallback, useState} from 'react'
import * as t from 'io-ts'
import {
  CardFilterFieldsBaseCodec,
  CardFilterFieldsBase
} from '../../common/cards/synced-card-feed-filter-query'
import {ButtonGroup, Form, FormSpacer} from '../../components/form'
import {Button} from '../../components/library/buttons'
import {Select, TextInput} from '../../components/library/form'
import {cardTemplateVersionName} from '../card-builder/helpers'
import {useSearchParam} from '../../hooks/use-search-param'
import {isRight} from 'fp-ts/lib/Either'
import {FlowConfig} from '../../hooks/use-action-flow-config'
import reporter from 'io-ts-reporters'
import {Dropdown} from '../../components/library/dropdown/dropdown'
import {
  DropdownBody,
  DropdownItem,
  DropdownSection
} from '../../components/library/dropdown/content'
import styled from 'styled-components'
import {Icon} from '../../components/library/icon/icon'
import SmallSearchIcon from '../../assets/icons/search-small.svg'
import {useFocusFirstFormInput} from '../../lib/hooks'
import SVGActionCard from '../../assets/icons/action-card-small.svg'

const DropdownWrapper = styled.a<{padding: string}>`
  padding: ${p => p.padding};
  border-radius: 4px;
  background-color: ${p => p.theme.colors.grey3};
  display: block;
`

const LightText = styled.span`
  color: ${p => p.theme.colors.grey10};
`

const LightTextSmall = styled.span`
  color: ${p => p.theme.colors.grey10};
  font-size: ${p => p.theme.fontSizeS};
`

const SearchBox = styled.form`
  border: 1px solid ${({theme}) => theme.grey10};
  border-radius: 4px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 16px 16px 12px;
`

const StyledSearchIcon = styled(SmallSearchIcon)`
  color: ${({theme}) => theme.black};
`

const SearchInput = styled.input`
  border: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  flex-grow: 1;
  font-size: ${({theme}) => theme.fontSizeXS};
`

const ActionFlowName = styled.div`
  padding-left: 20px;
  display: flex;
  align-items: center;
  height: 30px;
  font-size: ${({theme}) => theme.fontSizeXS};
`

const ActionCardName = styled.div<{searching: boolean}>`
  font-weight: ${p => (p.searching ? 'normal' : p.theme.fontWeights.bold)};
  display: flex;
  align-items: center;
  svg {
    // width: 16px;
    color: ${({theme}) => theme.colors.grey10};
    margin-right: 12px;
  }
`

const ItemId = styled.div`
  color: ${({theme}) => theme.colors.grey10};
`

const OptionItem = styled.a`
  font-size: ${({theme}) => theme.fontSizeXS};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px !important;
  height: 30px;
`

const ExtraSmallText = styled.span`
  font-size: ${({theme}) => theme.fontSizeXS};
`

// We need a value to match on when we want to match all versions of a card
// we can't use null or undefined value as it generates an unusable <option>.
// Versioning starts from version 1, so 0 will never match a valid version.
const ALL_VERSIONS_VERSION = 0

// We need a value to match on when we want to match all cards in a flow
// we can't use null or undefined value as it generates an unusable <option>.
const ALL_CARDS_ID = 'all_cards'

// We need a value to match on when we want to match all Action Flows
// we can't use null or undefined value as it generates an unusable <option>.
const ALL_FLOWS_ID = 'all_action_cards'

// just check for any filter criteria for now, could make this smarter if needed
const isValidState = (state: CardFilterFieldsBase) =>
  Object.values(state).some(
    value =>
      value !== undefined &&
      value !== null &&
      ((typeof value === 'string' && value.trim() !== '') ||
        (typeof value === 'number' && value >= 0)) // for cardTemplateVersion
  )

type CancellationFormProps = {
  actionFlowConfigs: FlowConfig[]
  reviewCancellation: (criteria: CardFilterFieldsBase) => void
  error?: string
}
export const CancellationForm = ({
  actionFlowConfigs,
  error,
  reviewCancellation
}: CancellationFormProps) => {
  const [cardSearch, setCardSearch] = useState('')
  const [flowSearch, setFlowSearch] = useState('')
  const [criteria, setCriteriaParams] = useSearchParam('criteria')
  const setCriteria = useCallback(
    (c: CardFilterFieldsBase) => setCriteriaParams(JSON.stringify(c)),
    [setCriteriaParams]
  )
  const decoded = CardFilterFieldsBaseCodec.decode(JSON.parse(criteria ?? '{}'))
  const decodedCriteria = isRight(decoded) ? decoded.right : {}
  const hasCriteria = Object.keys(decodedCriteria).length > 0

  const isValid = isValidState(decodedCriteria)
  const enabled = isValid && hasCriteria
  const loading = false

  const versionOptions = actionFlowConfigs
    ?.find(config => config.id === decodedCriteria.flowConfigId)
    ?.versions.nodes.map(version => ({
      value: version.version,
      text: `Version ${version.version}`
    }))
  // eslint-disable-next-line functional/immutable-data
  versionOptions?.unshift({value: ALL_VERSIONS_VERSION, text: 'All versions'})

  // Spread to new array to avoid mutating old array
  const sortedFlowConfigs = [...actionFlowConfigs].sort((a, b) => {
    return a.updated && b.updated ? (a.updated > b.updated ? -1 : 1) : 0
  })

  const cardTemplateOptions = sortedFlowConfigs
    .filter(
      config =>
        !decodedCriteria.flowConfigId ||
        decodedCriteria.flowConfigId === config.id
    )
    .flatMap(config =>
      Object.entries(config.currentVersion.config.cardTemplates ?? {}).map(
        ([id, template]) => ({
          value: JSON.stringify({
            cardTemplateId: id,
            actionFlowConfigId: config.id
          }),
          text: cardTemplateVersionName(template.metadata.cardName),
          flowConfigId: config.id,
          cardTemplateId: id
        })
      )
    )
  // eslint-disable-next-line functional/immutable-data
  const allCardsOption = {
    value: ALL_CARDS_ID,
    text: 'All cards',
    flowConfigId: ALL_FLOWS_ID
  }
  const cardSearchRegex = cardSearch
    ? new RegExp(`(${cardSearch})`, 'i')
    : undefined
  const filteredCardTemplateOptions = cardSearchRegex
    ? cardTemplateOptions?.filter(option => cardSearchRegex.test(option.text))
    : cardTemplateOptions

  const flowConfigOptions = sortedFlowConfigs.map(config => ({
    value: config.id,
    text: `${config.name || 'Unnamed'}`
  }))
  // eslint-disable-next-line functional/immutable-data
  flowConfigOptions?.unshift({value: ALL_FLOWS_ID, text: 'All Action Flows'})
  const flowSearchRegex = flowSearch
    ? new RegExp(`(${flowSearch})`, 'i')
    : undefined
  const filteredFlowConfigOptions = flowSearchRegex
    ? flowConfigOptions?.filter(option => flowSearchRegex.test(option.text))
    : flowConfigOptions

  const getDropdownText = useCallback(
    (value: string | undefined, upLabel: string, label: string) => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          {value ? (
            <>
              <LightTextSmall>{upLabel}</LightTextSmall>
              <br />
              {value}
            </>
          ) : (
            <LightText>{label}</LightText>
          )}
        </div>
        <div
          style={{
            float: 'right',
            marginRight: '25px'
          }}
        >
          <Icon name="chevron" rotate={90} />
        </div>
      </div>
    ),
    []
  )

  return (
    <Form
      onSubmit={() => {
        reviewCancellation(decodedCriteria)
      }}
      error={error}
      isValid={enabled}
      loading={loading}
      renderButtons={null}
    >
      <Dropdown
        trigger={(handleClick, ref) => (
          <DropdownWrapper
            padding={
              decodedCriteria.flowConfigId
                ? '10px 0 10px 20px;'
                : '20px 0 20px 20px;'
            }
            onClick={handleClick}
            ref={ref as React.Ref<HTMLAnchorElement>}
          >
            {getDropdownText(
              flowConfigOptions.find(
                option => option.value === decodedCriteria.flowConfigId
              )?.text,
              'Action Flow',
              'Select Action Flow...'
            )}
          </DropdownWrapper>
        )}
      >
        {closeDropdown => (
          <>
            <DropdownBody>
              <Search value={flowSearch} onChange={setFlowSearch} />
              {filteredFlowConfigOptions.map(f => {
                const display = getSearchDisplay(
                  f.text,
                  flowSearch,
                  flowSearchRegex
                )
                return (
                  <DropdownItem
                    key={`flow-config-${f.value}`}
                    // Ensure white space contnues to show when searching
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    <OptionItem
                      onClick={() => {
                        if (f.value === ALL_FLOWS_ID) {
                          setCriteria({
                            ...decodedCriteria,
                            cardTemplateId: undefined,
                            cardTemplateVersion: undefined,
                            flowConfigId: undefined
                          })
                          closeDropdown()
                          return
                        }
                        setCriteria({
                          ...decodedCriteria,
                          flowConfigId: f.value,
                          ...(!f.value ||
                          (decodedCriteria.cardTemplateId &&
                            !cardTemplateInFlow(
                              f.value,
                              decodedCriteria.cardTemplateId,
                              actionFlowConfigs
                            ))
                            ? {
                                cardTemplateId: undefined,
                                cardTemplateVersion: undefined
                              }
                            : {})
                        })
                        closeDropdown()
                      }}
                    >
                      <div>{display}</div>
                      {f.value !== ALL_FLOWS_ID ? (
                        <ItemId>ID: {f.value}</ItemId>
                      ) : null}
                    </OptionItem>
                  </DropdownItem>
                )
              })}
            </DropdownBody>
          </>
        )}
      </Dropdown>

      <FormSpacer />

      <Dropdown
        trigger={(handleClick, ref) => (
          <DropdownWrapper
            padding={
              decodedCriteria.flowConfigId && decodedCriteria.cardTemplateId
                ? '10px 0 10px 20px;'
                : '20px 0 20px 20px;'
            }
            onClick={handleClick}
            ref={ref as React.Ref<HTMLAnchorElement>}
          >
            {getDropdownText(
              cardTemplateOptions.find(
                option =>
                  option.value ===
                  JSON.stringify({
                    cardTemplateId: decodedCriteria.cardTemplateId,
                    actionFlowConfigId: decodedCriteria.flowConfigId
                  })
              )?.text,
              'Card',
              'Select card...'
            )}
          </DropdownWrapper>
        )}
      >
        {closeDropdown => (
          <>
            <DropdownBody>
              <Search value={cardSearch} onChange={setCardSearch} />
              {/* Don't show the "all cards" option when searching */}
              {!cardSearchRegex ? (
                <DropdownSection key={`dropdown-section-${ALL_FLOWS_ID}`}>
                  <DropdownItem
                    key={`card-template-${allCardsOption.value}`}
                    // Ensure white space contnues to show when searching
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    <a
                      onClick={() => {
                        setCriteria({
                          ...decodedCriteria,
                          cardTemplateId: undefined,
                          cardTemplateVersion: undefined
                        })
                        closeDropdown()
                      }}
                    >
                      <ExtraSmallText>
                        {getSearchDisplay(
                          allCardsOption.text,
                          cardSearch,
                          cardSearchRegex
                        )}
                      </ExtraSmallText>
                    </a>
                  </DropdownItem>
                </DropdownSection>
              ) : null}
              {actionFlowConfigs.map(flowConfig => {
                const flowCardTemplateOptions =
                  filteredCardTemplateOptions.filter(
                    c => c.flowConfigId === flowConfig.id
                  )
                if (!flowCardTemplateOptions.length) {
                  return null
                }
                return (
                  <DropdownSection key={`dropdown-section-${flowConfig.id}`}>
                    <ActionFlowName>
                      {flowConfig.name || 'Unnamed'}
                    </ActionFlowName>
                    {flowCardTemplateOptions.map(c => {
                      const display = getSearchDisplay(
                        c.text,
                        cardSearch,
                        cardSearchRegex
                      )
                      return (
                        <DropdownItem
                          key={`card-template-${flowConfig.id}-${c.value}`}
                          // Ensure white space contnues to show when searching
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          <OptionItem
                            onClick={() => {
                              if (c.value === ALL_CARDS_ID) {
                                setCriteria({
                                  ...decodedCriteria,
                                  cardTemplateId: undefined,
                                  cardTemplateVersion: undefined
                                })
                                closeDropdown()
                                return
                              }

                              const decodedValue = t
                                .type({
                                  cardTemplateId: t.string,
                                  actionFlowConfigId: t.string
                                })
                                .decode(JSON.parse(c.value))

                              if (!isRight(decodedValue)) {
                                console.error(
                                  'Invalid options for card template Id',
                                  {
                                    value: c.value,
                                    errors: reporter.report(decodedValue)
                                  }
                                )
                                return
                              }

                              const {cardTemplateId, actionFlowConfigId} =
                                decodedValue.right
                              // set to the latest published version by default
                              const versions =
                                actionFlowConfigs.find(
                                  c => c.id === actionFlowConfigId
                                )?.versions.nodes ?? []

                              const cardTemplateVersion =
                                versions?.find(n => n.status === 'PUBLISHED') ??
                                versions[0]

                              setCriteria({
                                ...decodedCriteria,
                                cardTemplateId,
                                flowConfigId: actionFlowConfigId,
                                // set to the latest published version by default
                                cardTemplateVersion:
                                  cardTemplateVersion?.version
                              })
                              closeDropdown()
                            }}
                          >
                            <ActionCardName searching={!!cardSearchRegex}>
                              <SVGActionCard />
                              {display}
                            </ActionCardName>
                            <ItemId>ID: {c.cardTemplateId}</ItemId>
                          </OptionItem>
                        </DropdownItem>
                      )
                    })}
                  </DropdownSection>
                )
              })}
            </DropdownBody>
          </>
        )}
      </Dropdown>

      <FormSpacer />

      <Select<number>
        disabled={!decodedCriteria.cardTemplateId}
        upLabel="Card template version"
        label="Select card template version..."
        firstOptionDisabled={true}
        options={versionOptions ?? []}
        value={decodedCriteria.cardTemplateVersion}
        onChange={cardTemplateVersion =>
          setCriteria({
            ...decodedCriteria,
            cardTemplateVersion:
              cardTemplateVersion === ALL_VERSIONS_VERSION
                ? undefined
                : cardTemplateVersion
          })
        }
      />

      <FormSpacer />

      <TextInput
        upLabel="Customer ID"
        label="Enter customer ID..."
        value={decodedCriteria.userId ?? ''}
        onChange={userId =>
          setCriteria({
            ...decodedCriteria,
            userId: userId || undefined
          })
        }
      />

      <FormSpacer />

      {/* Note excluding lifecycleId here, as it's now an internal thing */}

      <TextInput
        upLabel="Card instance ID"
        label="Enter card instance ID..."
        value={decodedCriteria.cardInstanceId ?? ''}
        onChange={cardInstanceId =>
          setCriteria({
            ...decodedCriteria,
            cardInstanceId: cardInstanceId || undefined
          })
        }
      />

      <FormSpacer />

      <TextInput
        upLabel="Action Flow invocation ID"
        label="Enter Action Flow invocation ID..."
        value={decodedCriteria.flowInvocationId ?? ''}
        onChange={flowInvocationId =>
          setCriteria({
            ...decodedCriteria,
            flowInvocationId: flowInvocationId || undefined
          })
        }
      />

      <FormSpacer />

      <TextInput
        upLabel="Action Flow instance ID"
        label="Enter Action Flow instance ID..."
        value={decodedCriteria.flowInstanceId ?? ''}
        onChange={flowInstanceId =>
          setCriteria({
            ...decodedCriteria,
            flowInstanceId: flowInstanceId || undefined
          })
        }
      />

      <ButtonGroup>
        <Button
          type="submit"
          size="large"
          disabled={!enabled}
          data-testid="review-before-cancelling"
        >
          Review before cancelling
        </Button>
        <Button
          onClick={() => {
            setCriteria({})
          }}
          appearance="secondary"
          size="large"
          disabled={!hasCriteria}
        >
          Reset criteria
        </Button>
      </ButtonGroup>
    </Form>
  )
}

const Search = ({
  onChange,
  value
}: {
  onChange: (text: string) => void
  value: string
}) => {
  const ref = useFocusFirstFormInput()
  return (
    <SearchBox onSubmit={e => e.preventDefault()} ref={ref}>
      <StyledSearchIcon />
      <SearchInput
        onChange={e => onChange(e.target.value)}
        placeholder="Search..."
        type="search"
        value={value}
      />
    </SearchBox>
  )
}

const cardTemplateInFlow = (
  flowConfigId: string,
  cardTemplateId: string,
  actionFlowConfigs: FlowConfig[]
) =>
  actionFlowConfigs.find(c => c.id === flowConfigId)?.currentVersion.config
    .cardTemplates?.[cardTemplateId]

const getSearchDisplay = (
  originalText: string,
  search: string | undefined,
  searchRegex: RegExp | undefined
) => {
  if (!search || !searchRegex) {
    return originalText
  }
  // Split the name into parts, and wrap the parts that match the search text <strong>
  const parts = originalText.split(searchRegex)

  return parts.map((part, index) =>
    searchRegex.test(part) ? <strong key={index}>{part}</strong> : part
  )
}
